import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "manager", "sales_rep", "finance_manager", "service_advisor", "registration_clerk", "customer"];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales_rep" });
  const [savingRoleFor, setSavingRoleFor] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data || []); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/auth/register", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "sales_rep" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    setSavingRoleFor(u.id);
    try {
      const { data } = await api.patch(`/users/${u.id}`, { role: newRole });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      toast.success(`${u.name || u.email} → ${newRole.replace("_", " ")}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingRoleFor(null);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((us) => us.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AdminShell title="User Management">
      <div className="flex justify-between mb-6">
        <div className="label-eyebrow">{users.length} users</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta rounded-sm" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B1324] border-white/10">
            <DialogHeader><DialogTitle className="font-heading text-xl">Create User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-sm">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" data-testid="user-name-input" /></div>
              <div><Label className="text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" data-testid="user-email-input" /></div>
              <div><Label className="text-sm">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#030712] border-white/10 mt-1 h-10" data-testid="user-password-input" /></div>
              <div><Label className="text-sm">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-[#030712] border-white/10 h-10 mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={create} className="btn-cta rounded-sm w-full" data-testid="save-user-btn">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="surface-elev text-left">
            <tr>
              <th className="px-4 py-3 label-eyebrow">Name</th>
              <th className="px-4 py-3 label-eyebrow">Email</th>
              <th className="px-4 py-3 label-eyebrow">Role</th>
              <th className="px-4 py-3 label-eyebrow">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-400">{u.email}</td>
                <td className="px-4 py-3">
                  {isAdmin && u.id !== me?.id ? (
                    <Select
                      value={u.role}
                      onValueChange={(v) => changeRole(u, v)}
                      disabled={savingRoleFor === u.id}
                    >
                      <SelectTrigger
                        className="bg-[#04060d] border-white/10 h-8 w-[180px] text-xs"
                        data-testid={`role-select-${u.id}`}
                      >
                        {savingRoleFor === u.id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <SelectValue />}
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0e1a] border-white/10">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} data-testid={`role-option-${u.id}-${r}`}>
                            {r.replace("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="pill pill-gold" title={u.id === me?.id ? "You cannot change your own role" : "Admin only"}>
                      {u.role?.replace("_", " ")}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => del(u)}
                    disabled={u.id === me?.id}
                    title={u.id === me?.id ? "You cannot delete your own account" : "Delete user"}
                    className="text-[#E63946] hover:text-[#E63946] disabled:opacity-30"
                    data-testid={`delete-user-${u.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  );
}
